import type { Cell, CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { FieldPath } from 'react-hook-form';
import { type FieldArrayWithId, type UseFormReturn } from 'react-hook-form';

import type { DeleteAndUndoCellProps } from 'af-root/scenes/Company/Shared/Materials/DeleteAndUndoCell';
import DeleteAndUndoCell from 'af-root/scenes/Company/Shared/Materials/DeleteAndUndoCell';

import { QuantityUnitLabel } from '@acceligentllc/shared/enums/quantityUnit';

import { numberFormatter } from 'af-utils/format.util';

import SelectAll from 'af-components/Controls/SelectAll';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import { DisplayCell } from 'af-components/RHFTableCellComponents/Input/DisplayCell';
import type { InputCellType } from 'af-components/RHFTableCellComponents/Input/types';
import type { SimpleTableProps } from 'af-components/RHFTableCellComponents/SimpleTable/types';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';

import BaseCostInput from './BaseCostInput';
import CheckboxCell from './CheckboxCell';
import { createValidFromGlyph } from './createValidFromGlyph';
import { type JobMaterialsFormInput, type JobMaterialsFormOutput, type JobMaterialFM, JobMaterialFMBaseCostHistorySchema } from './formModel';
import JobMaterialRateHistoryButton from './JobMaterialRateHistoryButton';
import RateInput from './RateInput';
import styles from './styles.module.scss';

export * from './formModel';

interface OwnProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: UseFormReturn<JobMaterialsFormInput, any, JobMaterialsFormOutput>;
	readonly: boolean;
	fields: FieldArrayWithId<JobMaterialsFormInput, 'materials', 'id'>[];
	selectedMaterials: Record<string, true>;
	onMaterialCheck: (id: number) => void;
	onRatePreview: (material: JobMaterialFM) => void;
	onRemove: (index: number) => void;
	removeScheduledCost: (materialId: number) => void;
	clearSelectedMaterials: () => void;
	selectAllMaterials: () => void;
	selectedMaterialCount: number;
	totalMaterialCount: number;
}

type Props = OwnProps;

type JobMaterialColumn = keyof JobMaterialFM;

type MaterialRateColumn = 'shippingMarkup' | 'salesMarkup' | 'tax';

type MaterialFieldPath = FieldPath<JobMaterialsFormInput>;

type MaterialCell = JobMaterialFM & SimpleTableRow;

const JobMaterialsTableForm: React.FC<Props> = (props) => {
	const {
		fields,
		readonly,
		form,
		selectedMaterials,
		onMaterialCheck,
		onRatePreview,
		onRemove,
		removeScheduledCost,
		clearSelectedMaterials,
		selectAllMaterials,
		selectedMaterialCount,
		totalMaterialCount,
	} = props;

	const {
		control,
		getValues,
		unregister,
		setError,
		clearErrors,
	} = form;

	const validateField = React.useCallback(async (path: MaterialFieldPath) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const [_unused1, _unused2, rateTypeField, rateField] = path.split('.');
		if (rateTypeField !== 'currentRate' && rateTypeField !== 'futureRate') {
			return;
		}
		const value = getValues(path);
		const PartialSchema = JobMaterialFMBaseCostHistorySchema.pick({ [rateField]: true });
		const { error } = PartialSchema.safeParse({ [rateField]: value });
		if (error) {
			setError(path, { message: error.message });
		} else {
			clearErrors(path);
		}
	}, [clearErrors, getValues, setError]);

	const renderCheckboxCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const { jobMaterialId } = cell.row.original;
		const isChecked = !!selectedMaterials[jobMaterialId];

		if (!readonly) {
			return null;
		}

		return (
			<CheckboxCell
				id={jobMaterialId}
				isChecked={isChecked}
				onChange={onMaterialCheck}
			/>
		);
	}, [onMaterialCheck, readonly, selectedMaterials]);

	const renderReadonlyInputCell = React.useCallback((
		cellName: JobMaterialColumn,
		cellType: InputCellType
	) => (cell: CellContext<MaterialCell, string>) => {
		return (
			<InputCellRenderer
				control={control}
				defaultValue={cell.getValue() ?? '-'}
				fieldsLength={fields.length}
				getValues={getValues}
				name={`materials.${cell.row.original.index}.${cellName}`}
				readonly={true}
				rhfId={cell.row.original.id}
				type={cellType}
				unregister={unregister}
			/>
		);
	}, [control, fields.length, getValues, unregister]);

	const renderTotalCostCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const { currentRate: { totalCost }, futureRate } = cell.row.original;

		return (
			<div className={styles.cell}>
				<div className={styles.cellLine}>
					<DisplayCell
						elementKey="totalCost"
						name="totalCost"
						readonly={true}
						type="dollar"
						value={numberFormatter(2, 2).format(totalCost)}
					/>
				</div>
				{!!futureRate && (
					<div className={styles.cellLine}>
						<DisplayCell
							className={styles.inactiveReadonlyField}
							elementKey="totalCost"
							name="totalCost"
							prefixGlyphs={[createValidFromGlyph(futureRate.validFrom)]}
							readonly={true}
							type="dollar"
							value={numberFormatter(2, 2).format(futureRate.totalCost)}
						/>
					</div>
				)}
			</div>
		);
	}, []);

	const renderRateCell = React.useCallback((
		cellName: MaterialRateColumn
	) => (cell: CellContext<MaterialCell, string>) => {
		const { futureRate, index, currentRate, id } = cell.row.original;

		const resolvedCurrentRateCellLineClassName = `${styles.cellLine} ${(!readonly && !futureRate) ? styles.inEdit : ''}`;
		const resolvedFutureRateCellLineClassName = `${styles.cellLine} ${!readonly ? styles.inEdit : ''}`;

		return (
			<div className={styles.cell}>
				<RateInput
					containerClassName={resolvedCurrentRateCellLineClassName}
					control={control}
					displayOnly={!!futureRate}
					fieldsLength={fields.length}
					getValues={getValues}
					name={`materials.${index}.currentRate.${cellName}`}
					onChange={validateField}
					rate={cellName}
					rateData={currentRate}
					readonly={readonly}
					rhfId={id}
					unregister={unregister}
				/>
				{futureRate && (
					<RateInput
						containerClassName={resolvedFutureRateCellLineClassName}
						control={control}
						displayOnly={false}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`materials.${index}.futureRate.${cellName}`}
						onChange={validateField}
						rate={cellName}
						rateData={futureRate}
						readonly={readonly}
						readonlyFieldClassName={readonly ? styles.inactiveReadonlyField : undefined}
						rhfId={cell.row.original.id}
						unregister={unregister}
						validFrom={futureRate.validFrom}
					/>
				)}
			</div>
		);
	}, [control, fields.length, getValues, readonly, unregister, validateField]);

	const renderGroupCell = React.useCallback((cellName: JobMaterialColumn) => (cell: CellContext<MaterialCell, string>) => {
		const name = `materials.${cell.row.original.index}.${cellName}` as const;

		return (
			<DisplayCell
				elementKey={name}
				name={name}
				readonly={true}
				value={cell.getValue()}
			/>
		);
	}, []);

	const renderUnitCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const name = `materials.${cell.row.original.index}.unit`;

		const unitLabel = QuantityUnitLabel[cell.getValue()];

		return (
			<DisplayCell
				elementKey={name}
				name={name}
				readonly={true}
				value={unitLabel}
			/>
		);
	}, []);

	const renderItemNumber = React.useCallback((cell: CellContext<MaterialCell, number>) => {
		return (
			<DisplayCell
				elementKey="totalCost"
				name="totalCost"
				readonly={true}
				value={cell.getValue()}
			/>
		);
	}, []);

	const renderVendorCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const name = `materials.${cell.row.original.index}.vendorName`;
		return (
			<DisplayCell
				elementKey={name}
				name={name}
				readonly={true}
				value={cell.getValue()}
			/>
		);
	}, []);

	const renderBaseCostCell = React.useCallback(() => (cell: CellContext<MaterialCell, string>) => {
		const { currentRate, index, id, futureRate } = cell.row.original;

		const resolvedFutureRateCellLineClassName = `${styles.cellLine} ${!readonly ? styles.inEdit : ''}`;

		return (
			<div className={styles.cell}>
				<BaseCostInput
					containerClassName={resolvedFutureRateCellLineClassName}
					control={control}
					defaultValue={currentRate.baseCost}
					displayOnly={!!futureRate}
					fieldsLength={fields.length}
					getValues={getValues}
					name={`materials.${index}.currentRate.baseCost`}
					onChange={validateField}
					readonly={readonly}
					rhfId={id}
					unregister={unregister}
				/>
				{futureRate && (
					<BaseCostInput
						containerClassName={resolvedFutureRateCellLineClassName}
						control={control}
						defaultValue={futureRate.baseCost}
						displayOnly={false}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`materials.${index}.futureRate.baseCost`}
						onChange={validateField}
						readonly={readonly}
						readonlyFieldClassName={readonly ? styles.inactiveReadonlyField : undefined}
						rhfId={cell.row.original.id}
						unregister={unregister}
						validFrom={futureRate.validFrom}
					/>
				)}
			</div>
		);
	}, [control, fields.length, getValues, readonly, unregister, validateField]);

	const renderRatePreviewCell = React.useCallback((cell: CellContext<MaterialCell, Nullable<number>>) => {
		return (
			<JobMaterialRateHistoryButton material={cell.row.original} onRateHistory={onRatePreview} />
		);
	}, [onRatePreview]);

	const renderDeleteAndUndoTomorrowEditCell = React.useCallback((cell: CellContext<MaterialCell, number>) => {
		const { original: material } = cell.row;
		const { index } = cell.row.original;

		let deleteAndUndoCellProps: DeleteAndUndoCellProps = {};

		if (readonly) {
			const { materialId, futureRate } = material;

			if (!!futureRate) {
				deleteAndUndoCellProps = {
					...deleteAndUndoCellProps,
					withResetChanges: true,
					materialId,
					onResetChanges: removeScheduledCost,
				};
			}
		} else {
			deleteAndUndoCellProps = {
				...deleteAndUndoCellProps,
				withDelete: true,
				index,
				onDelete: onRemove,
			};
		}

		return (
			<DeleteAndUndoCell {...deleteAndUndoCellProps} />
		);
	}, [onRemove, readonly, removeScheduledCost]);

	const renderSelectAllHeader = React.useCallback(() => {
		return (
			<SelectAll
				onClear={clearSelectedMaterials}
				onSelectAll={selectAllMaterials}
				selectedCount={selectedMaterialCount}
				totalCount={totalMaterialCount}
			/>
		);
	}, [clearSelectedMaterials, selectAllMaterials, selectedMaterialCount, totalMaterialCount]);

	const columns = React.useMemo<SimpleTableProps<MaterialCell>['columns']>(() => {
		return [
			{
				id: 'selected',
				isDisplayColumn: true,
				header: renderSelectAllHeader,
				cell: renderCheckboxCell,
				size: 40,
			},
			{
				id: 'group1',
				accessor: 'group1',
				header: 'Group 1 *',
				cell: renderGroupCell('group1'),
			},
			{
				id: 'group2',
				accessor: 'group2',
				header: 'Group 2',
				cell: renderGroupCell('group2'),
			},
			{
				id: 'group3',
				accessor: 'group3',
				header: 'Group 3',
				cell: renderGroupCell('group3'),
			},
			{
				id: 'vendorId',
				accessor: 'vendorName',
				header: 'Vendor',
				cell: renderVendorCell,
			},
			{
				id: 'vendorUniqueId',
				accessor: 'vendorUniqueId',
				header: 'Vendor ID',
				cell: renderReadonlyInputCell('vendorUniqueId', 'text'),
			},
			{
				id: 'tags',
				accessor: 'tags',
				header: 'Tags',
				cell: renderReadonlyInputCell('tags', 'text'),
			},
			{
				id: 'itemNumber',
				accessor: 'itemNumber',
				header: 'Item Number',
				cell: renderItemNumber,
				size: 90,
			},
			{
				id: 'displayName',
				accessor: 'displayName',
				header: 'Display Name *',
				cell: renderReadonlyInputCell('displayName', 'text'),
				size: 160,
			},
			{
				id: 'description',
				accessor: 'description',
				header: 'Description',
				cell: renderReadonlyInputCell('description', 'text'),
				size: 160,
			},
			{
				id: 'unit',
				accessor: 'unit',
				header: 'Unit *',
				cell: renderUnitCell,
			},
			{
				id: 'baseCost',
				accessor: 'baseCost',
				header: 'Base Cost *',
				cell: renderBaseCostCell(),
				externalClickControl: true,
			},
			{
				id: 'shippingMarkup',
				accessor: 'shippingMarkup',
				header: 'Shipping Markup',
				cell: renderRateCell('shippingMarkup'),
				size: 130,
				externalClickControl: true,
			},
			{
				id: 'salesMarkup',
				accessor: 'salesMarkup',
				header: 'Sales Markup',
				cell: renderRateCell('salesMarkup'),
				size: 130,
				externalClickControl: true,
			},
			{
				id: 'tax',
				accessor: 'tax',
				header: 'Tax',
				cell: renderRateCell('tax'),
				size: 130,
				externalClickControl: true,
			},
			{
				id: 'totalCost',
				accessor: 'totalCost',
				header: 'Total Cost',
				cell: renderTotalCostCell,
			},
			{
				id: 'ratePreview',
				accessor: 'jobMaterialId',
				header: '',
				cell: renderRatePreviewCell,
				size: 100,
			},
			{
				id: 'deleteAndUndo',
				accessor: 'id',
				header: '',
				cell: renderDeleteAndUndoTomorrowEditCell,
				size: 40,
			},
		];
	}, [
		renderCheckboxCell,
		renderGroupCell,
		renderReadonlyInputCell,
		renderItemNumber,
		renderRateCell,
		renderTotalCostCell,
		renderUnitCell,
		renderVendorCell,
		renderRatePreviewCell,
		renderBaseCostCell,
		renderDeleteAndUndoTomorrowEditCell,
		renderSelectAllHeader,
	]);

	const getRowId = React.useCallback((row: MaterialCell) => {
		return row.id;
	}, []);

	const getCellClassName = React.useCallback((cell: Cell<MaterialCell, unknown>) => {
		if (cell.column.id === 'selected') {
			return styles.selectCell;
		}
		return '';
	}, []);

	return (
		<SimpleTableRHFWrapper
			className={styles.MaterialsTable}
			columns={columns}
			fields={fields}
			formAccessorName="materials"
			getCellClassName={getCellClassName}
			getRowId={getRowId}
			readonly={readonly}
		/>
	);
};

export default React.memo(JobMaterialsTableForm);
